import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api";
import { Check, Copy, RotateCw } from "lucide-react";
import { useCallback, useRef, useState } from "react";

// One error UI, three variants. Replaces the old shadcn-Alert based
// `ErrorComponent` and the scattered `<p className="text-destructive">` snippets
// across the app. In dev, surfaces the full stack with a copy button so
// pasting into a chat/issue is one click. In prod, shows the message only.
//
// Variants:
// - `fullscreen` — fills the viewport (route-level boundary).
// - `page`       — fills the available content area inside DashboardLayout.
// - `inline`     — compact, sits inside a form, dialog, or section.
//
// All variants share the same anatomy: title, optional helper line,
// destructive-tinted error block (copyable in dev), and an actions row.

type ErrorViewVariant = "fullscreen" | "page" | "inline";

export type ErrorViewProps = {
  error: unknown;
  variant?: ErrorViewVariant;
  title?: string;
  /** When provided, a "Try again" button appears in the actions row. */
  onRetry?: () => void;
  /** Show a "Go home" link. Defaults to true on fullscreen/page, false on inline. */
  showHome?: boolean;
  className?: string;
};

function isNetworkError(error: unknown): boolean {
  if (error instanceof ApiError && error.code === "NETWORK_ERROR") return true;
  if (error instanceof TypeError && /fetch|network/i.test(error.message)) return true;
  return false;
}

function extractMessage(error: unknown): string {
  if (isNetworkError(error)) return "Unable to reach the server. Check your connection and try again.";
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message || "Something went wrong.";
  if (typeof error === "string") return error || "Something went wrong.";
  if (error == null) return "Something went wrong.";
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function extractDebugInfo(error: unknown): string {
  if (error instanceof ApiError) return `${error.status} ${error.code}: ${error.message}\n${error.stack ?? ""}`;
  if (error instanceof Error) return error.stack ?? error.message;
  return String(error);
}

export function ErrorView({
  error,
  variant = "page",
  title,
  onRetry,
  showHome,
  className,
}: ErrorViewProps) {
  const isDev = import.meta.env.DEV;
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const message = extractMessage(error);
  const raw = isDev ? extractDebugInfo(error) : message;

  const copyError = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (insecure context, denied permission). No-op
      // — the user can still select text in the <pre> below.
    }
  }, [raw]);

  const homeVisible = showHome ?? variant !== "inline";
  const isInline = variant === "inline";
  const resolvedTitle = title ?? "Something went wrong";

  // Sizing wrapper differs per variant; the inner content is shared.
  const wrapperClass =
    variant === "fullscreen"
      ? "flex min-h-screen flex-1 flex-col items-center justify-center p-6"
      : variant === "page"
        ? "flex flex-1 min-h-0 flex-col items-center justify-center p-6"
        : "w-full";

  const innerClass = isInline
    ? "w-full flex flex-col gap-2"
    : "w-full max-w-2xl flex flex-col gap-4";

  return (
    <div className={`${wrapperClass} ${className ?? ""}`.trim()}>
      <div className={innerClass}>
        {!isInline && (
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{resolvedTitle}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isDev
                ? "Here's the raw error so you can debug. Copy it for the team if needed."
                : "We couldn't load this view. Try again, and if it keeps happening let support know."}
            </p>
          </div>
        )}

        {isDev ? (
          <div className="flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/5">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-destructive/40">
              <span className="text-xs font-medium text-destructive">
                {isInline ? resolvedTitle : "Error"}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={copyError}
              >
                {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <pre
              className={`px-3 py-2 text-xs font-mono whitespace-pre-wrap break-words text-destructive overflow-auto ${
                isInline ? "max-h-40" : "max-h-[60vh]"
              }`}
            >
              {raw}
            </pre>
          </div>
        ) : (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {message}
          </div>
        )}

        {(onRetry || homeVisible) && (
          <div className="flex gap-2">
            {onRetry && (
              <Button onClick={onRetry} size={isInline ? "sm" : "default"} className="gap-2">
                <RotateCw className={isInline ? "size-3.5" : "size-4"} />
                Try again
              </Button>
            )}
            {homeVisible && (
              <Button asChild variant="outline" size={isInline ? "sm" : "default"}>
                <a href="/">Go home</a>
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
